import { inject, Injectable } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';

import { UserService } from '@smarthead-api/services/user.service';

import { LoginWithRedirect, StartLoadingUserData, UserDataLoaded, UserLogIn, UserLogOut } from './app.actions';

import { CommonLoggedUser } from '../../../../../shared/state/app.state';
import { environment } from '../../../environments/environment';

export interface AppStateModel {
  loggedUser: CommonLoggedUser | null;
}

@State<AppStateModel>({
  name: 'app',
  defaults: {
    loggedUser: null
  }
})
@Injectable()
export class AppState {
  readonly #authService = inject(AuthService);
  readonly #userService = inject(UserService);

  @Selector()
  static getUser(state: AppStateModel): CommonLoggedUser | null {
    return state.loggedUser ? state.loggedUser : null;
  }

  @Selector()
  static isUserLogged(state: AppStateModel): boolean {
    return !!state.loggedUser;
  }

  @Selector()
  static isUserAdmin(state: AppStateModel): boolean {
    return state.loggedUser ? state.loggedUser.sh_admin : false;
  }

  @Action(LoginWithRedirect)
  loginWithRedirect(_ctx: StateContext<AppStateModel>, { options }: LoginWithRedirect) {
    this.#authService.loginWithRedirect({
      ...options,
      appState: {
        ...options?.appState,
        target: options?.appState?.target || window.location.origin
      },
      authorizationParams: {
        ...options?.authorizationParams,
        ui_locales: options?.authorizationParams?.ui_locales || 'en'
      }
    });
  }

  @Action(UserLogOut)
  userLogOut(_ctx: StateContext<AppStateModel>, { options }: UserLogOut) {
    this.#authService.logout({
      ...options,
      logoutParams: {
        ...options?.logoutParams,
        returnTo: options?.logoutParams?.returnTo || window.location.origin
      }
    });
  }

  @Action(UserLogIn)
  userLogIn({ patchState, dispatch }: StateContext<AppStateModel>, { accessToken, expiresIn, idToken, idTokenPayload }: UserLogIn) {
    if (!idTokenPayload) {
      idTokenPayload = jwtDecode(idToken);
    }

    const appMetaData =
      idTokenPayload[`${environment.auth0_custom_claims_prefix}app_metadata`] || idTokenPayload[`${environment.auth0_custom_claims_prefix}appMetadata`];

    patchState({
      loggedUser: {
        accessToken,
        expiresIn,
        idToken,
        email: idTokenPayload['email'],
        sh_admin: appMetaData['sh_admin'] && appMetaData['sh_admin'] === 1,
        appMetaData,
        // @ts-ignore,
        userData: null
      }
    });

    dispatch(new StartLoadingUserData());
  }

  @Action(StartLoadingUserData)
  startLoadingUserData({ dispatch }: StateContext<AppStateModel>, {}: StartLoadingUserData) {
    this.#userService.getMe().subscribe(
      user => {
        dispatch(new UserDataLoaded(user));
      },
      () => {
        console.error(`Cannot load user data`);
        dispatch(new UserLogOut());
      }
    );
  }

  @Action(UserDataLoaded)
  userDataLoaded({ patchState, getState }: StateContext<AppStateModel>, { userData }: UserDataLoaded) {
    const state = getState();

    if (state.loggedUser) {
      patchState({
        loggedUser: {
          ...state.loggedUser,
          userData
        }
      });
    }
  }
}
